<script>
import Monitor from "@/services/Monitor";

export default {
  name: "TabDBCopy",
  data() {
    return {
      collapseState: false,
      isCopyLoading: false,
      copies: [],
      dbCopyForm: {
        tenant: "tenant",
        type: "common",
        output: "dump",
        limit: 100,
        db_name: "",
        test_tables: [],
        whitelist: [],
        blacklist: [],
        anonymize: [],
        test_users: [],
      },
      copyToDelete: null,
      showDeleteModal: false,
    };
  },
  computed: {
    iconClass() {
      return this.collapseState ? "mdi mdi-chevron-up" : "mdi mdi-chevron-down";
    }
  },
  async mounted() {
    await this.getCopies();
  },
  methods: {
    toggleCollapse() {
      this.collapseState = !this.collapseState;
    },
    addItem(array) {
      array.push('');
    },
    removeItem(array, index) {
      array.splice(index, 1);
    },
    async getCopies() {
      try {
        const res = await Monitor.getCopyData();
        this.copies = res.data;
      } catch (e) {
        console.error(e);
      }
    },
    async submitDBCopy() {
      try {
        this.isCopyLoading = true;
        const body = { ...this.dbCopyForm };
        await Monitor.requestDBCopy(body);
        await this.getCopies();
      } catch (e) {
        console.error(e);
      } finally {
        this.isCopyLoading = false;
      }
    },
    async downloadCopy(copy) {
      try {
        const response = await Monitor.downloadCopy(copy.id);

        const blob = new Blob([response.data], { type: response.headers["content-type"] });
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;

        const disposition = response.headers["content-disposition"];
        let filename = copy.file_name || "download.sql";

        if (disposition && disposition.indexOf("filename=") !== -1) {
          filename = disposition.split("filename=")[1].replace(/['"]/g, "");
        }

        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (err) {
        console.error("Download failed", err);
      }
    },
    confirmDelete(copy) {
      this.copyToDelete = copy;
      this.showDeleteModal = true;
    },
    async deleteCopyConfirmed() {
      if (!this.copyToDelete) return;
      try {
        await Monitor.deleteCopy(this.copyToDelete.id);
        this.$bvToast.toast("Copy deleted successfully", {
          variant: "success",
          solid: true,
        });
        await this.getCopies();
      } catch (e) {
        console.error("Delete failed", e);
        this.$bvToast.toast("Failed to delete copy", {
          variant: "danger",
          solid: true,
        });
      } finally {
        this.showDeleteModal = false;
        this.copyToDelete = null;
      }
    },
  }
};
</script>

<template>
  <div>
    <!-- Collapsible Header -->
    <div class="card p-3 mb-3">
      <a
        class="text-body fw-semibold pb-2 d-block"
        href="javascript:void(0);"
        role="button"
        aria-expanded="false"
        v-b-toggle.collapseDBCopy
        @click="toggleCollapse"
      >
        <i :class="iconClass" class="me-1"/> DB Copy Request Form
      </a>

        <b-collapse id="collapseDBCopy" class="mt-2">
        <div class="d-flex flex-column gap-3">
          <div class="d-flex flex-row gap-5 w-100 align-items-start">
              <!-- General Inputs -->
               <div class="d-flex flex-column w-50">
                 <h6>Config</h6>
                 <b-form-group class="mb-2" label="Tenant">
                   <b-form-input v-model="dbCopyForm.tenant" placeholder="Tenant" />
                 </b-form-group>

                 <b-form-group class="mb-2" label="Database">
                   <b-form-input v-model="dbCopyForm.db_name" placeholder="Database" />
                 </b-form-group>

                 <b-form-group class="mb-2" label="Type">
                   <b-form-input v-model="dbCopyForm.type" placeholder="Type" />
                 </b-form-group>
       
                 <b-form-group class="mb-2" label="Limit">
                   <b-form-input type="number" v-model.number="dbCopyForm.limit" placeholder="Limit" min="1" />
                 </b-form-group>
               </div>
            </div>
          </div>

        <!-- Arrays (Test tables, Whitelist, Blacklist, Anonymize) -->
        <div class="row mt-3">

          <div class="col-md-3">
            <h6>Test Users</h6>
            <div v-for="(table, index) in dbCopyForm.test_users" :key="index" class="d-flex mb-1">
              <b-form-input v-model="dbCopyForm.test_users[index]" placeholder="Table name" />
              <b-button size="sm" variant="danger" @click="removeItem(dbCopyForm.test_users, index)">X</b-button>
            </div>
            <b-button size="sm" variant="success" @click="addItem(dbCopyForm.test_users)">Add User</b-button>
          </div>

          <div class="col-md-3">
            <h6>Test Tables</h6>
            <div v-for="(table, index) in dbCopyForm.test_tables" :key="index" class="d-flex mb-1">
              <b-form-input v-model="dbCopyForm.test_tables[index]" placeholder="Table name" />
              <b-button size="sm" variant="danger" @click="removeItem(dbCopyForm.test_tables, index)">X</b-button>
            </div>
            <b-button size="sm" variant="success" @click="addItem(dbCopyForm.test_tables)">Add Table</b-button>
          </div>

          <div class="col-md-3">
            <h6>Whitelist</h6>
            <div v-for="(item, index) in dbCopyForm.whitelist" :key="index" class="d-flex mb-1">
              <b-form-input v-model="dbCopyForm.whitelist[index]" placeholder="Item" />
              <b-button size="sm" variant="danger" @click="removeItem(dbCopyForm.whitelist, index)">X</b-button>
            </div>
            <b-button size="sm" variant="success" @click="addItem(dbCopyForm.whitelist)">Add</b-button>
          </div>

          <div class="col-md-3">
            <h6>Blacklist</h6>
            <div v-for="(item, index) in dbCopyForm.blacklist" :key="index" class="d-flex mb-1">
              <b-form-input v-model="dbCopyForm.blacklist[index]" placeholder="Item" />
              <b-button size="sm" variant="danger" @click="removeItem(dbCopyForm.blacklist, index)">X</b-button>
            </div>
            <b-button size="sm" variant="success" @click="addItem(dbCopyForm.blacklist)">Add</b-button>
          </div>

          <div class="col-md-3">
            <h6>Anonymize</h6>
            <div v-for="(item, index) in dbCopyForm.anonymize" :key="index" class="d-flex mb-1">
              <b-form-input v-model="dbCopyForm.anonymize[index]" placeholder="Item" />
              <b-button size="sm" variant="danger" @click="removeItem(dbCopyForm.anonymize, index)">X</b-button>
            </div>
            <b-button size="sm" variant="success" @click="addItem(dbCopyForm.anonymize)">Add</b-button>
          </div>
        </div>

        <!-- Submit -->
        <div class="mt-3 text-end">
          <b-button variant="primary" @click="submitDBCopy">Request DB Copy</b-button>
        </div>
      </b-collapse>

    </div>

    <!-- Copies Table -->
    <div class="card">
      <div v-if="isCopyLoading" class="text-center my-3">
        <b-spinner />
      </div>
      <div v-else>
        <b-table
          :items="copies"
          :fields="[
            { key: 'file_name', label: 'File Name' },
            { key: 'file_size', label: 'Size (bytes)' },
            { key: 'status', label: 'Status' },
            { key: 'created_at', label: 'Created At' },
            { key: 'actions', label: 'Download' },
            { key: 'delete', label: 'Delete' }
          ]"
          responsive="sm"
          show-empty
          empty-text="No backups available"
        >
          <template #cell(actions)="row">
            <b-button size="sm" variant="primary" @click="downloadCopy(row.item)">
              Download
            </b-button>
          </template>

          <template #cell(delete)="row">
            <b-button size="sm" variant="danger" @click="confirmDelete(row.item)">
              <i class="uil uil-trash-alt font-size-18"></i>
            </b-button>
          </template>
        </b-table>
      </div>
    </div>
    <!-- Delete Confirmation Modal -->
    <b-modal
      v-model="showDeleteModal"
      title="Confirm Deletion"
      ok-title="Delete"
      ok-variant="danger"
      @ok="deleteCopyConfirmed"
    >
      <p>Are you sure you want to delete <strong>{{ copyToDelete && copyToDelete.file_name }}</strong>?</p>
    </b-modal>
  </div>
</template>
